





































































































































// libs
import { Vue, Component } from 'vue-property-decorator';
import { State } from 'vuex-class';
import moment from 'moment';
import lodash from 'lodash';

// helpers
import ApiHelper from 'api-helper';
import Toast from '@common/helpers/toast';
import { isRevertCancelRequestEnabled as resolveRevertCancelRequestEnabled } from '@/common/helpers/prescription-button-state';

// interfaces
import { Order } from '@/models/order';
import { Patient } from '@/models/patient';
import { User } from '@/models/user';

// constants
import { DELIVERY_STATUS, DELIVERY_STATUS_NAME, REVERT_CANCEL_REQUEST_MODAL, USER_TREATMENT_STATE } from '@/common/constants/auth';
import { PER_PAGE } from '@common/constants/pagination';

// components
import PageLoader from '@components/common/PageLoader.vue';
import Pagination from '@components/common/Pagination.vue';
import LazyloadImage from '@/components/image/LazyloadImage.vue';
import ModalCancelDelivery from '@/components/v2/patient/delivery/ModalCancelDelivery.vue';
import ModalChangePlan from '@components/v2/patient/delivery/ModalChangePlan.vue';
import CancelRequestBar from '@/components/v2/patient/about/cancel_request/CancelRequestBar.vue';
import { UserTreatment } from '@/models/user-treatment';

@Component({
  components: {
    PageLoader,
    ModalCancelDelivery,
    ModalChangePlan,
    Pagination,
    LazyloadImage,
    CancelRequestBar,
  },
})

export default class DeliveryInfoChannel extends Vue {
  @State((state) => state.patient_detail.data) patient!: Patient;
  @State((state) => state.auth.currentUser) currentUser!: User;
  @State((state) => state.treatment_detail.data) activeTreatment: UserTreatment;

  private DELIVERY_STATUS = DELIVERY_STATUS;
  private orderList: Order[] | [] = [];
  private isLoading: boolean = false;
  private isSubmitting: boolean = false;
  private isRevertingCancelRequest: boolean = false;
  private REVERT_CONFIRM_MODAL_NAME = REVERT_CANCEL_REQUEST_MODAL.DELIVERY;

  private totalPage: number = 1;
  private page: number = 1;
  private activeOrder: Order | null = null;

  private get isRevertCancelRequestVisible(): boolean {
    return this.currentUser.isCSRole
      && resolveRevertCancelRequestEnabled(this.activeTreatment.state);
  }

  async created() {
    this.$store.commit('treatment_detail/setLoadingChild', true);
    await this.getOrderList();
    this.$store.commit('treatment_detail/setLoadingChild', false);
  }

  private async getOrderList() {
    try {
      this.isLoading = true;
      const payload = {
        user_treatment_id: this.activeTreatment.id,
        with: 'plan,tracking,user,userTreatment.menu',
        page: this.page,
        per_page: PER_PAGE,
      };

      const response = await ApiHelper
        .getApi('PatientV2Api')
        .getDeliveryList(this.patient.id, payload);
      this.orderList = response.data.map((item) => new Order().deserialize(item));
      this.totalPage = response.meta.last_page;
      this.isLoading = false;
    } catch (error) {
      if (error.message === 'cancel') {
        return;
      }
      this.isLoading = false;
    }
  }

  private pageChanged(page) {
    this.page = page;
    this.getOrderList();
  }

  private changeActiveOrder(order) {
    this.activeOrder = order;
  }

  private handleHiddenModal(modalName) {
    this.$modal.hide(modalName);
  }

  private handleShowModal(modalName, order) {
    // this.changeActiveOrder(order);
    this.$modal.show(modalName, { order });
  }

  private goToDetail(delivery) {
    if (this.currentUser.isDoctorRole) {
      return;
    }
    this.$router.push({path: `/deliveries/${delivery.id}`});
  }

  private canCancelOrder(listDelivery) {
    const state = [DELIVERY_STATUS.NOT_PAID, DELIVERY_STATUS.ERROR_PAID];
    // Doctors can change plans (see canChangePlan) but must NOT be able to
    // cancel orders — that is an admin/CS-only operation (No.143 review).
    const canCancel = this.currentUser.isSuperAdminRole
      || this.currentUser.isAdminRole
      || this.currentUser.isCSRole;
    return canCancel && lodash.some(listDelivery, (data) => state.includes(data.state));
  }

  private canChangePlan(order) {
    return !order.plan.is_shampoo_subscription
      && !order.isCancelled
      && order.tracking.some((tracking) => tracking.paid_at != null)
      && this.checkRole()
      && [USER_TREATMENT_STATE.DOCTOR_PRESCRIPTION_OK, USER_TREATMENT_STATE.SUBSCRIPTION_CONTRACT].includes(this.activeTreatment.state);
  }

  // Roles that may initiate a plan change (admin, CS, doctor, super-admin).
  // Do NOT reuse this for canCancelOrder — doctors must not cancel orders.
  private checkRole() {
    return this.currentUser.isSuperAdminRole
      || this.currentUser.isAdminRole
      || this.currentUser.isCSRole
      || this.currentUser.isDoctorRole;
  }

  private getDeliveryState(state) {
    return DELIVERY_STATUS_NAME[state];
  }

  private deliveryDate(delivery) {
    return moment(delivery).format('YYYY年 M月 D日');
  }

  private cancelOrder() {
    if (this.activeOrder && this.activeOrder.id) {
      this.isLoading = true;
      this.isSubmitting = true;
      ApiHelper.getApi('DoctorMouthApi').updateOrderStatus(this.activeOrder.id)
        .then(() => {
          this.getOrderList();
          Toast.success('更新しました');
        })
        .catch(() => {
          Toast.error('ビデオ診療予定日は担当医とCSのみ編集できます。');
        })
        .finally(() => {
          this.handleHiddenModal('modalCancelDelivery');
          this.isLoading = false;
          this.isSubmitting = false;
        });
    }
  }

  private changeOrder(e) {
    this.isSubmitting = true;
    const self = this;

    ApiHelper
      .getApi('OrderChangeApi')
      .changeOrder(this.activeOrder?.id, e)
      .then(async () => {
        this.$modal.hide('modalChangePlan');
        this.page = 1;
        await this.getOrderList();

        // Sort descending by id before searching so .find() returns the most recent order
        // with this plan — without sorting the list may contain an older cancelled/succeeded
        // order before the one RecreateFeature just created, causing stale plan data to bind.
        const changedOrder = [...this.orderList]
          .sort((a, b) => Number(b.id) - Number(a.id))
          .find((order) => Number(order.plan?.id) === Number(e.plan_id));
        const nextState = e.re_monshin === true
          ? USER_TREATMENT_STATE.SELECTED_TREATMENT
          : this.activeTreatment.state;
        // Update treatment_detail (owns activeTreatment) so plan/state changes are reflected
        // immediately in this view. Also sync user_treatment_list for the sidebar list.
        // When re_monshin is requested, clear latest_answer on every reservation so the UI
        // does not show the stale answer date from the previous questionnaire cycle.
        const reservationsForDetail = e.re_monshin === true
          ? (this.activeTreatment.reservations ?? []).map((r) => ({ ...r, latest_answer: undefined }))
          : this.activeTreatment.reservations;
        this.$store.commit('treatment_detail/setTreatment', {
          ...this.activeTreatment,
          state: nextState,
          plan: changedOrder?.plan ?? this.activeTreatment.plan,
          reservations: reservationsForDetail,
        });
        this.$store.commit('user_treatment_list/applyPlanChanged', {
          id: this.activeTreatment.id,
          state: nextState,
          plan: changedOrder?.plan,
          reMonshin: e.re_monshin,
        });
        Toast.success('更新しました');
      })
      .catch((error: any) => {
        Toast.error(error.response.data.message);
      })
      .finally(function() {
        self.isSubmitting = false;
      });
  }

  private async revertCancelRequest() {
    try {
      this.isRevertingCancelRequest = true;
      const res = await ApiHelper.getApi('UserTreatmentApi')
        .revertCancelRequest(this.activeTreatment.id);
      this.activeTreatment.state = res.data.state;
      this.$store.commit('user_treatment_list/setUserTreatmentState', {
        id: this.activeTreatment.id,
        state: res.data.state,
      });
      Toast.success('キャンセルを撤回しました');
    } catch (error) {
      const message = (error as any)?.response?.data?.message || 'エラーが発生しました';
      Toast.error(message);
    } finally {
      this.isRevertingCancelRequest = false;
    }
  }
}
